// ── A PDF on screen, one page at a time (owner 2026-11-02, "Document Attachments & Linked Records") ──────────────────
// What the attachments viewer draws with (components/CompanyWorkspace/attachments.tsx): pages to a canvas at any zoom, and a first page as a
// thumbnail. pdf.js and its worker are the project's own (public/pdf.worker.min.js), loaded when first asked. FileLibrary, DueDiligence and
// SlipCell each keep their older stacked copy of the loader: they have no page navigation to share.
//
// ⛔ isEvalSupported: false, here and in those three (review 2026-11-03). An uploaded PDF is whatever somebody uploaded, and this pdf.js (3.x) would
// otherwise compile a font's matrix with new Function (CVE-2024-4367). Drawn this way, a crafted file is a broken picture and never script on this origin.
//
// ⛔ A CANVAS HAS A LIMIT AND DOES NOT SAY SO. Past it the browser draws nothing and throws nothing, so a large page at 300% is an empty white sheet. The backing store stops at
// the cap and the CSS size stays, as pdf.js' own maxCanvasPixels does: past the cap a page is softer, never blank.
const MAX_CANVAS_PIXELS = 60_000_000;
/** iOS Safari stops at 16.7 MP, and a coarse pointer is the nearest thing a page can ask. */
const MAX_CANVAS_PIXELS_TOUCH = 16_000_000;
/** How many backing pixels to a CSS pixel for a page of this size on this screen, under the cap. */
export const backingScale = (cssWidth: number, cssHeight: number, dpr: number, coarse: boolean): number => {
  const cap = coarse ? MAX_CANVAS_PIXELS_TOUCH : MAX_CANVAS_PIXELS;
  const area = Math.max(1, cssWidth * cssHeight);
  return Math.min(dpr, Math.sqrt(cap / area));
};
let pdfLibP: Promise<any> | null = null;
const getPdfLib = (): Promise<any> => {
  if (!pdfLibP) {
    pdfLibP = import('pdfjs-dist/legacy/build/pdf').then((m: any) => {
      const lib = m.default || m;
      lib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      return lib;
    });
  }
  return pdfLibP;
};

export type PdfHandle = {
  pages: number;
  /** A page's width at 100%, in CSS pixels. */
  pageWidth: (n: number) => Promise<number>;
  /** Draws page n on the canvas at `scale`, sharp on this screen. Asking again cancels a draw still running. */
  render: (n: number, canvas: HTMLCanvasElement, scale: number) => Promise<void>;
  destroy: () => void;
};

export async function openPdf(data: ArrayBuffer): Promise<PdfHandle> {
  const pdfjs = await getPdfLib();
  // pdf.js takes the buffer over to its worker, so it is handed a copy.
  const doc = await pdfjs.getDocument({ data: data.slice(0), isEvalSupported: false }).promise;
  let task: any = null;
  return {
    pages: doc.numPages,
    pageWidth: async n => (await doc.getPage(n)).getViewport({ scale: 1 }).width,
    render: async (n, canvas, scale) => {
      if (task) { try { task.cancel(); } catch { /* finished */ } }
      const page = await doc.getPage(n);
      const dpr = Math.max(1, Number(window.devicePixelRatio) || 1);
      const css = page.getViewport({ scale });
      const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
      const vp = page.getViewport({ scale: scale * backingScale(css.width, css.height, dpr, coarse) });
      canvas.width = Math.floor(vp.width);
      canvas.height = Math.floor(vp.height);
      canvas.style.width = `${Math.floor(css.width)}px`;
      canvas.style.height = `${Math.floor(css.height)}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no canvas');
      const mine = page.render({ canvasContext: ctx, viewport: vp });
      task = mine;
      try { await mine.promise; } catch (e: any) { if (e?.name !== 'RenderingCancelledException') throw e; }
    },
    destroy: () => { try { doc.destroy(); } catch { /* gone */ } },
  };
}

/** The first page as a small picture, for a file card. */
export async function pdfThumbUrl(data: ArrayBuffer, width: number): Promise<string> {
  const h = await openPdf(data);
  try {
    const canvas = document.createElement('canvas');
    await h.render(1, canvas, width / (await h.pageWidth(1)));
    return canvas.toDataURL('image/png');
  } finally { h.destroy(); }
}
